import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthStore } from './store/authStore';
import { useInventoryStore } from './store/inventoryStore';
import useStatisticsStore from './store/statisticsStore';
import { useProfileStore } from './store/profileStore';
import { useSearchStore } from './store/searchStore';
import { useSupplyStore } from './store/supplyStore';
import GlobalStyles from './styles/GlobalStyles';
import LoadingManager from './components/LoadingManager';
import { useGlobalLoading } from './hooks/useGlobalLoading';
import { ToastProvider } from './components/ToastContainer';
import { SubscriptionErrorProvider } from './hooks/useSubscriptionError';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import Statistics from './pages/Statistics';
import Profile from './pages/Profile';
import Tariffs from './pages/Tariffs';
import Inventory from './pages/Inventory';
import Sales from './pages/Sales';
import Supply from './pages/Supply';
import Search from './pages/Search';
import SneakerDetails from './pages/SneakerDetails';
import ClothesDetails from './pages/ClothesDetails';
import StockroomItem from './pages/StockroomItem';
import Shipments from './pages/Shipments';
import NotFound from './pages/NotFound';

// Компонент для защищенных маршрутов
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export function App() {
  const { isAuthenticated, validateTokenOnInit } = useAuthStore();
  const { fetchInventory } = useInventoryStore();
  const { fetchStatistics } = useStatisticsStore();
  const { fetchProfile } = useProfileStore();
  const { loadProducts } = useSearchStore();
  const { addLoadingStep, removeLoadingStep, updateLoadingStep } = useGlobalLoading();
  const [isAppInitializing, setIsAppInitializing] = useState(true);

  useEffect(() => {
    const initializeApp = async () => {
      addLoadingStep('app-init', 'Готовим для вас всё самое лучшее...', 0);

      await validateTokenOnInit();

      const { isAuthenticated: authState } = useAuthStore.getState();

      if (authState) {
        // Определяем целевую страницу
        const currentPath = window.location.pathname;
        const targetPage = currentPath === '/' ? '/statistics' : currentPath;

        try {
          switch (targetPage) {
            case '/inventory':
              updateLoadingStep('app-init', 'Ищем пары на складе...');
              await fetchInventory(0, 5);
              break;
            case '/sales':
              updateLoadingStep('app-init', 'Загружаем проданные товары...');
              break;
            case '/supply':
              updateLoadingStep('app-init', 'Готовим страницу закупок...');
              break;
            case '/search':
              updateLoadingStep('app-init', 'Смотрим товары у продавцов...');
              await loadProducts(16, 0);
              break;
            case '/statistics':
              updateLoadingStep('app-init', 'Считаем вашу прибыль...');
              await fetchStatistics();
              break;
            case '/profile':
              updateLoadingStep('app-init', 'Загружаем ваш профиль...');
              await fetchProfile();
              break;
            default:
              if (targetPage.startsWith('/sneaker/') || targetPage === '/tariffs') {
                updateLoadingStep('app-init', 'Готовим данные...');
              }
              break;
          }
        } catch (error) {
          // Игнорируем ошибки предзагрузки
        }
      }

      removeLoadingStep('app-init');
      setIsAppInitializing(false);
    };

    initializeApp();
  }, [validateTokenOnInit, fetchInventory, fetchStatistics, fetchProfile, loadProducts, addLoadingStep, removeLoadingStep, updateLoadingStep]);

  return (
    <>
      <GlobalStyles />
      {isAppInitializing ? (
        <LoadingManager fullscreen />
      ) : (
        <>
          <LoadingManager fullscreen />
          <ToastProvider>
            <BrowserRouter>
              <SubscriptionErrorProvider>
                <Routes>
                  {/* Редирект с корневого пути на логин или профиль в зависимости от состояния авторизации */}
                  <Route
                    path="/"
                    element={
                      isAuthenticated ? (
                        <Navigate to="/profile" replace />
                      ) : (
                        <Navigate to="/login" replace />
                      )
                    }
                  />

                  {/* Публичные маршруты */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/recovery" element={<ResetPassword />} />

                  {/* Защищенные маршруты */}
                  <Route
                    path="/statistics"
                    element={
                      <ProtectedRoute>
                        <Statistics />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/inventory"
                    element={
                      <ProtectedRoute>
                        <Inventory />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/shipments"
                    element={
                      <ProtectedRoute>
                        <Shipments />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/sales"
                    element={
                      <ProtectedRoute>
                        <Sales />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/supply"
                    element={
                      <ProtectedRoute>
                        <Supply />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/sneaker/:id"
                    element={
                      <ProtectedRoute>
                        <SneakerDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/clothes/:id"
                    element={
                      <ProtectedRoute>
                        <ClothesDetails />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/tariffs"
                    element={
                      <ProtectedRoute>
                        <Tariffs />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/search"
                    element={
                      <ProtectedRoute>
                        <Search />
                      </ProtectedRoute>
                    }
                  />

                  {/* Не защищенный роут для просмотра товара по коду */}
                  <Route path="/item/:product_code" element={<StockroomItem />} />

                  {/* Маршрут для страниц не найдено */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </SubscriptionErrorProvider>
            </BrowserRouter>
          </ToastProvider>
        </>
      )}
    </>
  );
}

export default App;
